import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useEffect } from "react";

import { getSingleEmployeeQueryOptions } from "@/config/queries/query-options";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { Form } from "@/components/ui/form/form";
import { EmployeeDetailsForm } from "./_components/employee-details-form/employee-details-form";
import { EmployeeAddressContacts } from "./_components/employee-details-form/emp-address-contacts";
import { editEmployee } from "@/config/mutation/admin";
import { EmployeeAttendanceLeaves } from "./_components/employee-details-form/emp-attendance-leaves";
import { EmployeeSalary } from "./_components/employee-details-form/emp-salary";
import { EmployeePersonal } from "./_components/employee-details-form/emp-personal";
import { Button } from "@/components/ui/button/button";
import { EmployeeDocuments } from "./_components/employee-details-form/emp-documents";

const formSchema = z.object({
  // ? TAB Overview
  name: z
    .string({
      required_error: "Employee name required!",
    })
    .min(1, {
      message: "Employee name is required!",
    }),
  email: z
    .string({
      required_error: "Email is required!",
    })
    .email({
      message: "Invalid Email",
    }),
  password: z.string().optional(),

  role: z
    .string({
      required_error: "Employee role required!",
    })
    .min(1, {
      message: "Employee role is required!",
    }),
  photo: z.string().optional(),

  // ? TAB Address & Contacts
  address: z.string().optional(),
  phone: z.string().length(10, {
    message: "Phone number should of length 10 digit",
  }),
  emergencyContactNumber: z.string().length(10, {
    message: "Phone number should of length 10 digit",
  }),

  // ? TAB Attendance & Leaves
  holidayList: z.string().optional(),
  paidLeavesTotal: z.coerce.number().optional(),
  paidLeavesRemaining: z.coerce.number().optional(),
  leaveApprover: z.union([z.string(), z.null()]).optional(),
  allowanceApprover: z.union([z.string(), z.null()]).optional(),
  loanApprover: z.union([z.string(), z.null()]).optional(),

  // ? TAB Salary
  grossSalary: z.coerce.number().optional(),
  travellingExpenses: z.coerce.number().optional(),
  otherExpenses: z.coerce.number().optional(),
  paidAmount: z.coerce.number().optional(),

  // ? TAB Personal
  bloodGroup: z.string().optional(),
  employeeCode: z.string().optional(),
  panNumber: z.string().optional(),
  pfNumber: z.string().optional(),
  otherDetails: z.string().optional(),
  familyDetails: z.string().optional(),

  // ? TAB Profile
  offerLetter: z.string().optional(),
  promotionLetters: z.array(z.string()).optional(),
  officeRulesHandbook: z.array(z.string()).optional(),

  // ? TAB Documents
  documents: z
    .array(
      z.object({
        doc: z.string(),
        url: z.string(),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

const EditEmployeePage = () => {
  const params = useParams() as {
    empId: string;
  };
  const adminKey = Cookies.get("key") as string;

  const queryClient = useQueryClient();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // ? TAB Overview
      name: "",
      email: "",
      role: "",
      photo: "",
      password: "",

      // ? TAB Address & Contacts
      phone: "",
      emergencyContactNumber: "",
      address: "",

      // ? TAB Attendance & Leaves
      holidayList: "",
      paidLeavesTotal: 0,
      paidLeavesRemaining: 0,
      leaveApprover: "",
      allowanceApprover: "",
      loanApprover: "",

      // ? TAB Salary
      grossSalary: 0,
      travellingExpenses: 0,
      otherExpenses: 0,
      paidAmount: 0,

      // ? TAB Personal
      bloodGroup: "",
      employeeCode: "",
      panNumber: "",
      pfNumber: "",
      otherDetails: "",
      familyDetails: "",

      // ? TAB Profile
      offerLetter: "",
      promotionLetters: [],
      officeRulesHandbook: [],

      documents: [],
    },
  });

  // console.log(form.formState.errors);

  const { mutate, isPending: isEditing } = useMutation({
    mutationFn: editEmployee(adminKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    const postData = {
      name: values.name,
      email: values.email,
      phone: values.phone,
      role: values.role,
      password: values?.password,
      leaveApprover: values.leaveApprover === "" ? null : values.leaveApprover,
      allowanceApprover:
        values.allowanceApprover === "" ? null : values.allowanceApprover,
      loanApprover: values.loanApprover === "" ? null : values.loanApprover,
      grossSalary: values.grossSalary,
      travellingExpenses: values.travellingExpenses,
      otherExpenses: values.otherExpenses,
      photo: values?.photo,
      emergencyContactNumber: values.emergencyContactNumber,
      bloodGroup: values.bloodGroup,
      address: values.address,
      offerLetter: "",
      promotionLetters: [],
      officeRulesHandbook: [],
      paidLeavesTotal: values.paidLeavesTotal,
      paidLeavesRemaining: values.paidLeavesRemaining,
      documents: values?.documents,
      holidayList: values?.holidayList === "" ? null : values?.holidayList,
      employeeCode: values?.employeeCode,
      panNumber: values?.panNumber,
      pfNumber: values?.pfNumber,
      paidAmount: values?.paidAmount,
      otherDetails: values?.otherDetails,
      familyDetails: values?.familyDetails,
    };
    // console.log(postData);

    mutate(
      {
        empId: params.empId,
        postData,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["employees"],
          });
          await queryClient.invalidateQueries({
            queryKey: ["employee", params.empId],
          });

          toast.success("Details Saved!");
        },

        onError: (error) => {
          console.log("ERROR WHILE Saving employee details", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  const { data, isPending, isError, error } = useQuery(
    getSingleEmployeeQueryOptions(adminKey, params.empId)
  );

  useEffect(() => {
    form.setValue("name", data?.employee?.name as string);
    form.setValue("email", data?.employee?.email as string);
    form.setValue("phone", data?.employee?.phone as string);
    form.setValue("role", data?.employee?.role as string);
    form.setValue("leaveApprover", data?.employee?.leaveApprover as string);
    form.setValue(
      "allowanceApprover",
      data?.employee?.allowanceApprover as string
    );
    form.setValue("loanApprover", data?.employee?.loanApprover as string);
    form.setValue("loanApprover", data?.employee?.loanApprover as string);
    form.setValue("grossSalary", data?.employee?.grossSalary as number);
    form.setValue(
      "travellingExpenses",
      data?.employee?.travellingExpenses as number
    );
    form.setValue("otherExpenses", data?.employee?.otherExpenses as number);
    form.setValue("photo", data?.employee?.photo as string);
    form.setValue(
      "emergencyContactNumber",
      data?.employee?.emergencyContactNumber as string
    );
    form.setValue("bloodGroup", data?.employee?.bloodGroup as string);
    form.setValue("employeeCode", data?.employee?.employeeCode as string);
    form.setValue("panNumber", data?.employee?.panNumber as string);
    form.setValue("pfNumber", data?.employee?.pfNumber as string);
    form.setValue("paidAmount", data?.employee?.paidAmount as number);
    form.setValue("familyDetails", data?.employee?.familyDetails as string);
    form.setValue("otherDetails", data?.employee?.otherDetails as string);
    form.setValue("address", data?.employee?.address as string);
    form.setValue("offerLetter", data?.employee?.offerLetter as string);
    form.setValue(
      "promotionLetters",
      data?.employee?.promotionLetters as string[]
    );
    form.setValue("paidLeavesTotal", data?.employee?.paidLeavesTotal as number);
    form.setValue(
      "paidLeavesRemaining",
      data?.employee?.paidLeavesRemaining as number
    );
    form.setValue("documents", data?.employee?.documents);
    form.setValue("holidayList", data?.employee?.holidayList?._id);
  }, [data]);

  if (isPending) {
    return <div>Loading....</div>;
  }

  if (isError) {
    return <pre>{JSON.stringify(error)}</pre>;
  }

  return (
    <div className="p-5 md:h-[calc(100%-56px)] h-[calc(100vh-56px)] relative">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="p-4 flex items-center gap-4">
            {data?.employee?.photo ? (
              <div className="relative w-20 rounded-md">
                <img
                  src={data?.employee?.photo}
                  alt="Uploaded Image"
                  className="object-cover max-w-full rounded-md"
                />
              </div>
            ) : (
              <div className="w-20 h-20 rounded-full bg-accent flex items-center justify-center text-xl">
                {data?.employee?.name?.charAt(0)}
              </div>
            )}

            <div className="flex flex-col">
              <h3 className="text-2xl font-semibold">{data?.employee?.name}</h3>
              <p className="text-sm">{data?.employee?.email}</p>
            </div>
          </div>
          <div className="bg-card rounded-md p-3">
            <div className="flex flex-col relative">
              <div className="absolute right-10 z-[100] -top-1">
                <Button type="submit" disabled={isEditing}>
                  Save
                </Button>
              </div>
              <div className="">
                <Tabs defaultValue="details" className="mr-auto w-full">
                  <div className="flex items-center justify-between pb-3 sticky top-[56px] bg-card z-30">
                    <TabsList className="w-full justify-start rounded-none border-b border-b-gray-500 bg-transparent p-0">
                      <TabsTrigger
                        value="details"
                        className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Details
                      </TabsTrigger>
                      <TabsTrigger
                        value="address-contacts"
                        className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Address & Contacts
                      </TabsTrigger>
                      <TabsTrigger
                        value="attendance-leaves"
                        className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Attendance & Leaves
                      </TabsTrigger>
                      <TabsTrigger
                        value="salary"
                        className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Salary
                      </TabsTrigger>
                      <TabsTrigger
                        value="personal"
                        className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Personal
                      </TabsTrigger>
                      <TabsTrigger
                        value="documents"
                        className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        Documents
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="details" className="relative rounded-md">
                    <EmployeeDetailsForm />
                  </TabsContent>
                  <TabsContent
                    value="address-contacts"
                    className="relative rounded-md"
                  >
                    <EmployeeAddressContacts />
                  </TabsContent>
                  <TabsContent
                    value="attendance-leaves"
                    className="relative rounded-md"
                  >
                    <EmployeeAttendanceLeaves />
                  </TabsContent>
                  <TabsContent value="salary" className="relative rounded-md">
                    <EmployeeSalary />
                  </TabsContent>
                  <TabsContent value="personal" className="relative rounded-md">
                    <EmployeePersonal />
                  </TabsContent>
                  <TabsContent
                    value="documents"
                    className="relative rounded-md"
                  >
                    <EmployeeDocuments />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditEmployeePage;
